import type { EntityState, Reducer } from "@reduxjs/toolkit"

import { loadActiveFarmAsync } from "@/base/load-app"
import { createEntityAdapter, createSlice, createSelector } from "@reduxjs/toolkit"

import { signOutAsync } from "./auth.reducer"
import type { RootState } from "@/redux/root.reducer"

export type EndGunState = "ON" | "OFF"

export interface PivotEndGunTableSector {
  id: number
  endGunTableId: number
  startAngleDegrees: number
  endAngleDegrees: number
  endGunState: EndGunState
  sortOrder: number
  createDate: string
}

export interface PivotEndGunTable {
  id: number
  centerPivotId: number
  name: string
  isActive: boolean
  createDate: string
  sectors: PivotEndGunTableSector[]
}

const tablesAdapter = createEntityAdapter<PivotEndGunTable, number>({
  selectId: (table) => table.id,
  sortComparer: (a, b) => {
    if (a.isActive && !b.isActive) return -1
    if (b.isActive && !a.isActive) return 1
    return a.name.localeCompare(b.name)
  },
})

const sectorsAdapter = createEntityAdapter<PivotEndGunTableSector, number>({
  selectId: (sector) => sector.id,
  sortComparer: (a, b) => {
    if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder
    return a.startAngleDegrees - b.startAngleDegrees
  },
})

interface PivotEndGunTablesState {
  tables: EntityState<PivotEndGunTable, number>
  sectors: EntityState<PivotEndGunTableSector, number>
}

const initialState: PivotEndGunTablesState = {
  tables: tablesAdapter.getInitialState(),
  sectors: sectorsAdapter.getInitialState(),
}

const pivotEndGunTablesSlice = createSlice({
  name: "pivotEndGunTables",
  initialState,
  reducers: {
    upsertTable: (state, action: { payload: PivotEndGunTable }) => {
      const table = action.payload
      tablesAdapter.upsertOne(state.tables, table)

      const existingSectorIds = Object.values(state.sectors.entities)
        .filter((s) => s?.endGunTableId === table.id)
        .map((s) => s!.id)
      sectorsAdapter.removeMany(state.sectors, existingSectorIds)
      if (table.sectors) {
        sectorsAdapter.upsertMany(state.sectors, table.sectors)
      }
    },
    upsertManyTables: (state, action: { payload: PivotEndGunTable[] }) => {
      const tables = action.payload
      tablesAdapter.upsertMany(state.tables, tables)
      for (const table of tables) {
        const existingSectorIds = Object.values(state.sectors.entities)
          .filter((s) => s?.endGunTableId === table.id)
          .map((s) => s!.id)
        sectorsAdapter.removeMany(state.sectors, existingSectorIds)
        if (table.sectors) {
          sectorsAdapter.upsertMany(state.sectors, table.sectors)
        }
      }
    },
    removeTable: (state, action: { payload: number }) => {
      const tableId = action.payload
      tablesAdapter.removeOne(state.tables, tableId)
      const sectorIds = Object.values(state.sectors.entities)
        .filter((s) => s?.endGunTableId === tableId)
        .map((s) => s!.id)
      sectorsAdapter.removeMany(state.sectors, sectorIds)
    },
    upsertSector: (state, action: { payload: PivotEndGunTableSector }) => {
      sectorsAdapter.upsertOne(state.sectors, action.payload)
    },
    upsertManySectors: (state, action: { payload: PivotEndGunTableSector[] }) => {
      sectorsAdapter.upsertMany(state.sectors, action.payload)
    },
    removeSector: (state, action: { payload: number }) => {
      sectorsAdapter.removeOne(state.sectors, action.payload)
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(signOutAsync.fulfilled, () => {
        return initialState
      })
      .addCase(loadActiveFarmAsync.fulfilled, (state, { payload }) => {
        if (payload.pivotEndGunTables) {
          tablesAdapter.setAll(state.tables, payload.pivotEndGunTables)
          const allSectors: PivotEndGunTableSector[] = []
          for (const table of payload.pivotEndGunTables) {
            if (table.sectors) allSectors.push(...table.sectors)
          }
          if (allSectors.length > 0) {
            sectorsAdapter.setAll(state.sectors, allSectors)
          }
        }
      }),
})

export const {
  upsertTable,
  upsertManyTables,
  removeTable,
  upsertSector,
  upsertManySectors,
  removeSector,
} = pivotEndGunTablesSlice.actions

const tableSelectors = tablesAdapter.getSelectors<RootState>(
  (state) => state.pivotEndGunTables.tables,
)
const sectorSelectors = sectorsAdapter.getSelectors<RootState>(
  (state) => state.pivotEndGunTables.sectors,
)

export const {
  selectAll: selectAllPivotEndGunTables,
  selectById: selectPivotEndGunTableById,
} = tableSelectors

export const {
  selectAll: selectAllPivotEndGunTableSectors,
  selectById: selectPivotEndGunTableSectorById,
} = sectorSelectors

export function selectEndGunTablesForPivot(
  state: RootState,
  centerPivotId: number,
): PivotEndGunTable[] {
  return selectAllPivotEndGunTables(state).filter(
    (table) => table.centerPivotId === centerPivotId,
  )
}

export const selectEndGunTablesForPivotMemo = createSelector(
  [selectAllPivotEndGunTables, (_: RootState, centerPivotId: number) => centerPivotId],
  (tables, centerPivotId) =>
    tables.filter((table) => table.centerPivotId === centerPivotId),
)

export function selectSectorsForEndGunTable(
  state: RootState,
  endGunTableId: number,
): PivotEndGunTableSector[] {
  return selectAllPivotEndGunTableSectors(state).filter(
    (sector) => sector.endGunTableId === endGunTableId,
  )
}

export const pivotEndGunTablesReducer: Reducer<
  PivotEndGunTablesState
> = pivotEndGunTablesSlice.reducer
